import React, { useCallback, useState } from 'react';

import { BannerProvider, StatusBanner } from '@xingternal/banner';
import { useExperiments } from '@xingternal/hub';

import { UniversalRedirect } from '@/components/_common';
import { FullPageDotLoading } from '@/components/full-page-dot-loading';
import { GlobalError } from '@/components/global-error';
import {
  ApplyFormPageviewTrackingProvider,
  ApplyFormPrefilledTrackingProvider,
  ApplyFormUploadsProvider,
} from '@/domain/job-application/contexts';
import { JobUserXingApplicationStatus, VisibleJob, useJobXingApplicationDataQuery } from '@/graphql/generated';
import { setKey } from '@/helpers/storage';
import { useCrateFormExperiment, useLoginStates } from '@/hooks';

import {
  ApplyForm,
  JobApplicationPageTitle,
  JobApplicationXingFrame,
  SubmitConfirmation,
  SubmitError,
} from './components';
import {
  JobApplicationCvFileIdProvider,
  JobApplicationEmailProvider,
  JobApplicationFormSettingsContext,
  JobApplicationFullNameProvider,
  JobApplicationPrioritizedContext,
  JobApplicationRevenueTypeContext,
  SubmitConfirmationJobIdsProvider,
} from './contexts';
import { XingApplicationJob } from './types';

export const LO_APPLICATION_SUBMITTED_STORAGE_PREFIX = 'application-submitted';

type ApplicationStatus = 'form' | 'confirmation' | 'error';

type Props = {
  slug: string;
};

export const JobApplication: React.FC<Props> = ({ slug }) => {
  const [currentApplicationStatus, setCurrentApplicationStatus] = useState<ApplicationStatus>('form');
  const { loading, error, data, refetch } = useJobXingApplicationDataQuery({ variables: { slug } });
  const { loading: isLoadingExperiments } = useExperiments();
  const { isLoggedIn } = useLoginStates();

  const job = data?.job;
  const jobTitle = job?.__typename === 'VisibleJob' ? job.title : '';
  const application = (job as VisibleJob)?.userInteractions?.application;
  const didUserSuccessfullyXingApply =
    application?.status &&
    application.status !== JobUserXingApplicationStatus.Error &&
    application.status !== JobUserXingApplicationStatus.Undelivered;

  const handleSubmittedForm = useCallback(() => {
    !isLoggedIn &&
      setKey(`${LO_APPLICATION_SUBMITTED_STORAGE_PREFIX}:${slug}`, { submittedAt: new Date().toISOString() });
    setCurrentApplicationStatus('confirmation');
  }, [slug, isLoggedIn]);

  const handleSubmittedErrorForm = useCallback(() => {
    setCurrentApplicationStatus('error');
  }, []);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { isCrateFormRedirection } = useCrateFormExperiment(!!job?.prioritized);

  if (loading || isLoadingExperiments)
    return (
      <JobApplicationXingFrame jobApplicationTrackingStatus="loading">
        <FullPageDotLoading />
      </JobApplicationXingFrame>
    );

  if (error || (job && (job.__typename !== 'VisibleJob' || job.application?.__typename !== 'JobXingApplication'))) {
    return (
      <JobApplicationXingFrame jobApplicationTrackingStatus="loading_error">
        <GlobalError
          type="500"
          ctaTranslation="JOBS_POSTING_APPLY_FORM_ERROR_RETRY_CTA"
          ctaHandler={(): void => {
            refetch();
          }}
        />
      </JobApplicationXingFrame>
    );
  }

  // ABACUS-420 redirection in case the form is accessed by route
  if (!isLoggedIn && isCrateFormRedirection) {
    return <UniversalRedirect to={`/jobs/apply/${slug}`} />;
  }

  return (
    <>
      <JobApplicationPageTitle jobTitle={jobTitle} />
      <JobApplicationXingFrame jobApplicationTrackingStatus={currentApplicationStatus}>
        <JobApplicationPrioritizedContext.Provider value={job?.prioritized}>
          <JobApplicationRevenueTypeContext.Provider value={job as XingApplicationJob}>
            <JobApplicationFormSettingsContext.Provider value={(job as XingApplicationJob).application.formSettings}>
              <JobApplicationFullNameProvider>
                <JobApplicationEmailProvider>
                  <JobApplicationCvFileIdProvider>
                    {currentApplicationStatus === 'confirmation' || didUserSuccessfullyXingApply ? (
                      <SubmitConfirmationJobIdsProvider
                        jobId={job?.id as XingApplicationJob['id']}
                        jobGlobalId={job?.globalId as XingApplicationJob['globalId']}
                      >
                        <SubmitConfirmation />
                      </SubmitConfirmationJobIdsProvider>
                    ) : currentApplicationStatus === 'error' ? (
                      <SubmitError />
                    ) : (
                      <ApplyFormPageviewTrackingProvider>
                        <ApplyFormUploadsProvider>
                          <ApplyFormPrefilledTrackingProvider>
                            <BannerProvider
                              Component={StatusBanner}
                              defaultProps={{ display: 'fixed', variant: 'error' } as const}
                            >
                              <ApplyForm
                                job={job as XingApplicationJob}
                                showConfirmationStep={handleSubmittedForm}
                                showErrorStep={handleSubmittedErrorForm}
                              />
                            </BannerProvider>
                          </ApplyFormPrefilledTrackingProvider>
                        </ApplyFormUploadsProvider>
                      </ApplyFormPageviewTrackingProvider>
                    )}
                  </JobApplicationCvFileIdProvider>
                </JobApplicationEmailProvider>
              </JobApplicationFullNameProvider>
            </JobApplicationFormSettingsContext.Provider>
          </JobApplicationRevenueTypeContext.Provider>
        </JobApplicationPrioritizedContext.Provider>
      </JobApplicationXingFrame>
    </>
  );
};
